import React, { useEffect, useState, useContext } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import {
  courseService,
  assignmentService,
  quizService,
} from '../services/apiService'

const ManageCourse = () => {
  const { id } = useParams()
  const { user } = useContext(AuthContext)
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [lessons, setLessons] = useState([])
  const [assignments, setAssignments] = useState([])
  const [quizzes, setQuizzes] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('lessons')
  const [showAddLesson, setShowAddLesson] = useState(false)
  const [showAddAssignment, setShowAddAssignment] = useState(false)
  const [showAddQuiz, setShowAddQuiz] = useState(false)
  const [managingQuizId, setManagingQuizId] = useState(null)
  const [questions, setQuestions] = useState([])
  const [showAddQuestion, setShowAddQuestion] = useState(false)
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    points: 1,
    order: 0,
    answers: [{ answer_text: '', is_correct: false }],
  })
  const [lessonForm, setLessonForm] = useState({
    title: '',
    content: '',
    video_url: '',
    duration: '',
  })
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    due_date: '',
    max_score: 100,
  })
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    time_limit: 30,
    passing_score: 70,
  })

  useEffect(() => {
    fetchCourse()
    fetchLessons()
    fetchAssignments()
    fetchQuizzes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchCourse = async () => {
    try {
      const response = await courseService.getCourse(id)
      setCourse(response.data)

      // Check if user is the instructor
      if (response.data.instructor.id !== user?.id) {
        navigate('/my-courses')
      }
    } catch (error) {
      console.error('Error fetching course:', error)
      navigate('/my-courses')
    } finally {
      setLoading(false)
    }
  }

  const fetchLessons = async () => {
    try {
      const response = await courseService.getLessons(id)
      const data = response.data.results || response.data
      setLessons(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching lessons:', error)
    }
  }

  const fetchAssignments = async () => {
    try {
      const response = await assignmentService.getCourseAssignments(id)
      const data = response.data.results || response.data
      setAssignments(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching assignments:', error)
    }
  }

  const fetchQuizzes = async () => {
    try {
      const response = await quizService.getCourseQuizzes(id)
      const data = response.data.results || response.data
      setQuizzes(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching quizzes:', error)
    }
  }

  const handleLessonChange = (e) => {
    setLessonForm({
      ...lessonForm,
      [e.target.name]: e.target.value,
    })
  }

  const handleAddLesson = async (e) => {
    e.preventDefault()
    try {
      await courseService.createLesson(id, {
        ...lessonForm,
        order: lessons.length + 1,
        duration: parseInt(lessonForm.duration),
      })
      setLessonForm({ title: '', content: '', video_url: '', duration: '' })
      setShowAddLesson(false)
      fetchLessons()
      alert('Lesson added successfully!')
    } catch (error) {
      console.error('Error adding lesson:', error)
      alert('Failed to add lesson. Please try again.')
    }
  }

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) {
      return
    }
    try {
      await courseService.deleteLesson(id, lessonId)
      fetchLessons()
      alert('Lesson deleted successfully!')
    } catch (error) {
      console.error('Error deleting lesson:', error)
      alert('Failed to delete lesson.')
    }
  }

  const handleTogglePublish = async () => {
    try {
      await courseService.updateCourse(id, {
        is_published: !course.is_published,
      })
      fetchCourse()
      alert(
        course.is_published
          ? 'Course unpublished successfully!'
          : 'Course published successfully!',
      )
    } catch (error) {
      console.error('Error toggling publish status:', error)
      alert('Failed to update course status.')
    }
  }

  const handleAssignmentChange = (e) => {
    setAssignmentForm({
      ...assignmentForm,
      [e.target.name]: e.target.value,
    })
  }

  const handleAddAssignment = async (e) => {
    e.preventDefault()
    try {
      await assignmentService.createCourseAssignment(id, assignmentForm)
      setAssignmentForm({
        title: '',
        description: '',
        due_date: '',
        max_score: 100,
      })
      setShowAddAssignment(false)
      fetchAssignments()
      alert('Assignment added successfully!')
    } catch (error) {
      console.error('Error adding assignment:', error)
      alert('Failed to add assignment. Please try again.')
    }
  }

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return
    }
    try {
      await assignmentService.deleteAssignment(assignmentId)
      fetchAssignments()
      alert('Assignment deleted successfully!')
    } catch (error) {
      console.error('Error deleting assignment:', error)
      alert('Failed to delete assignment.')
    }
  }

  const handleQuizChange = (e) => {
    setQuizForm({
      ...quizForm,
      [e.target.name]: e.target.value,
    })
  }

  const handleAddQuiz = async (e) => {
    e.preventDefault()
    try {
      await quizService.createCourseQuiz(id, quizForm)
      setQuizForm({
        title: '',
        description: '',
        time_limit: 30,
        passing_score: 70,
      })
      setShowAddQuiz(false)
      fetchQuizzes()
      alert('Quiz added successfully!')
    } catch (error) {
      console.error('Error adding quiz:', error)
      alert('Failed to add quiz. Please try again.')
    }
  }

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) {
      return
    }
    try {
      await quizService.deleteQuiz(quizId)
      fetchQuizzes()
      alert('Quiz deleted successfully!')
    } catch (error) {
      console.error('Error deleting quiz:', error)
      alert('Failed to delete quiz.')
    }
  }

  const handleManageQuestions = async (quizId) => {
    setManagingQuizId(quizId)
    setShowAddQuestion(false)
    setQuestionForm({
      question_text: '',
      points: 1,
      order: 0,
      answers: [{ answer_text: '', is_correct: false }],
    })
    await fetchQuestions(quizId)
  }

  const fetchQuestions = async (quizId) => {
    try {
      const response = await quizService.getQuizQuestions(quizId)
      const data = response.data.results || response.data
      setQuestions(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching questions:', error)
    }
  }

  const handleQuestionChange = (e) => {
    setQuestionForm({
      ...questionForm,
      [e.target.name]: e.target.value,
    })
  }

  const handleAnswerChange = (index, field, value) => {
    const newAnswers = [...questionForm.answers]
    newAnswers[index][field] = value

    // If marking as correct, unmark all others
    if (field === 'is_correct' && value) {
      newAnswers.forEach((answer, i) => {
        if (i !== index) answer.is_correct = false
      })
    }

    setQuestionForm({ ...questionForm, answers: newAnswers })
  }

  const addAnswer = () => {
    setQuestionForm({
      ...questionForm,
      answers: [
        ...questionForm.answers,
        { answer_text: '', is_correct: false },
      ],
    })
  }

  const removeAnswer = (index) => {
    const newAnswers = questionForm.answers.filter((_, i) => i !== index)
    setQuestionForm({ ...questionForm, answers: newAnswers })
  }

  const handleAddQuestion = async (e) => {
    e.preventDefault()

    // Validate that at least one answer is correct
    const hasCorrectAnswer = questionForm.answers.some((a) => a.is_correct)
    if (!hasCorrectAnswer) {
      alert('Please mark at least one answer as correct')
      return
    }

    // Validate all answers have text
    const allAnswersHaveText = questionForm.answers.every((a) =>
      a.answer_text.trim(),
    )
    if (!allAnswersHaveText) {
      alert('Please fill in all answer fields')
      return
    }

    try {
      await quizService.createQuestion(managingQuizId, questionForm)
      setQuestionForm({
        question_text: '',
        points: 1,
        order: questions.length,
        answers: [{ answer_text: '', is_correct: false }],
      })
      setShowAddQuestion(false)
      await fetchQuestions(managingQuizId)
      await fetchQuizzes() // Refresh to update question count
      alert('Question added successfully!')
    } catch (error) {
      console.error('Error adding question:', error)
      alert('Failed to add question. Please try again.')
    }
  }

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return
    }
    try {
      await quizService.deleteQuestion(questionId)
      await fetchQuestions(managingQuizId)
      await fetchQuizzes() // Refresh to update question count
      alert('Question deleted successfully!')
    } catch (error) {
      console.error('Error deleting question:', error)
      alert('Failed to delete question.')
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    )
  }

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='mb-8'>
        <Link
          to='/my-courses'
          className='text-primary-600 hover:text-primary-700 mb-4 inline-flex items-center'
        >
          <svg
            className='h-5 w-5 mr-2'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M10 19l-7-7m0 0l7-7m-7 7h18'
            />
          </svg>
          Back to My Courses
        </Link>
        <h1 className='text-3xl font-bold text-gray-900 mt-4'>
          Manage Course: {course?.title}
        </h1>
        <p className='text-gray-600 mt-2'>
          Add and manage lessons, assignments, and quizzes for your course
        </p>
        <div className='mt-4 flex items-center'>
          <span className='text-sm text-gray-700 mr-3'>Course Status:</span>
          <button
            onClick={handleTogglePublish}
            className={`px-4 py-2 rounded-md font-medium ${
              course?.is_published
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
            }`}
          >
            {course?.is_published ? '✓ Published' : '⚠ Unpublished'}
          </button>
          <span className='ml-3 text-sm text-gray-500'>
            {course?.is_published
              ? 'Visible to students'
              : 'Not visible to students'}
          </span>
        </div>

        {/* Instructor Tools */}
        <div className='mt-6 flex flex-wrap gap-3'>
          <Link
            to={`/courses/${id}/students`}
            className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium'
          >
            <svg
              className='h-5 w-5 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
              />
            </svg>
            View Students
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className='border-b border-gray-200 mb-6'>
        <nav className='-mb-px flex space-x-8'>
          <button
            onClick={() => setActiveTab('lessons')}
            className={`${
              activeTab === 'lessons'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Lessons ({lessons.length})
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`${
              activeTab === 'assignments'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Assignments ({assignments.length})
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            className={`${
              activeTab === 'quizzes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Quizzes ({quizzes.length})
          </button>
        </nav>
      </div>

      {/* Lessons Tab */}
      {activeTab === 'lessons' && (
        <div>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-xl font-semibold'>Course Lessons</h2>
            <button
              onClick={() => setShowAddLesson(!showAddLesson)}
              className='bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700'
            >
              {showAddLesson ? 'Cancel' : '+ Add Lesson'}
            </button>
          </div>

          {showAddLesson && (
            <div className='bg-white p-6 rounded-lg shadow mb-6'>
              <h3 className='text-lg font-semibold mb-4'>Add New Lesson</h3>
              <form onSubmit={handleAddLesson} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Lesson Title *
                  </label>
                  <input
                    type='text'
                    name='title'
                    required
                    value={lessonForm.title}
                    onChange={handleLessonChange}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='e.g., Introduction to JavaScript'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Lesson Content *
                  </label>
                  <textarea
                    name='content'
                    required
                    value={lessonForm.content}
                    onChange={handleLessonChange}
                    rows={8}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='Enter the lesson content, instructions, and materials...'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Video URL (Optional)
                    </label>
                    <input
                      type='url'
                      name='video_url'
                      value={lessonForm.video_url}
                      onChange={handleLessonChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='https://youtube.com/...'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Duration (minutes) *
                    </label>
                    <input
                      type='number'
                      name='duration'
                      required
                      min='1'
                      value={lessonForm.duration}
                      onChange={handleLessonChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='30'
                    />
                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    className='bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700'
                  >
                    Add Lesson
                  </button>
                </div>
              </form>
            </div>
          )}

          {lessons.length > 0 ? (
            <div className='space-y-3'>
              {lessons.map((lesson, index) => (
                <div
                  key={lesson.id}
                  className='bg-white p-4 rounded-lg shadow border border-gray-200'
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <h3 className='font-semibold text-gray-900'>
                        {index + 1}. {lesson.title}
                      </h3>
                      <p className='text-sm text-gray-600 mt-1'>
                        Duration: {lesson.duration} minutes
                      </p>
                      {lesson.video_url && (
                        <p className='text-sm text-primary-600 mt-1'>
                          📹 Video included
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteLesson(lesson.id)}
                      className='text-red-600 hover:text-red-800 ml-4'
                    >
                      <svg
                        className='h-5 w-5'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-12 bg-gray-50 rounded-lg'>
              <svg
                className='mx-auto h-12 w-12 text-gray-400 mb-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                />
              </svg>
              <p className='text-gray-500 mb-4'>No lessons added yet</p>
              <button
                onClick={() => setShowAddLesson(true)}
                className='text-primary-600 hover:text-primary-700 font-medium'
              >
                Add your first lesson
              </button>
            </div>
          )}
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-xl font-semibold'>Course Assignments</h2>
            <button
              onClick={() => setShowAddAssignment(!showAddAssignment)}
              className='bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700'
            >
              {showAddAssignment ? 'Cancel' : '+ Add Assignment'}
            </button>
          </div>

          {showAddAssignment && (
            <div className='bg-white p-6 rounded-lg shadow mb-6'>
              <h3 className='text-lg font-semibold mb-4'>Add New Assignment</h3>
              <form onSubmit={handleAddAssignment} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Assignment Title *
                  </label>
                  <input
                    type='text'
                    name='title'
                    required
                    value={assignmentForm.title}
                    onChange={handleAssignmentChange}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='e.g., Build a React Component'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Description *
                  </label>
                  <textarea
                    name='description'
                    required
                    value={assignmentForm.description}
                    onChange={handleAssignmentChange}
                    rows={6}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='Enter assignment instructions, requirements, and submission guidelines...'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Due Date *
                    </label>
                    <input
                      type='datetime-local'
                      name='due_date'
                      required
                      value={assignmentForm.due_date}
                      onChange={handleAssignmentChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Max Score *
                    </label>
                    <input
                      type='number'
                      name='max_score'
                      required
                      min='1'
                      value={assignmentForm.max_score}
                      onChange={handleAssignmentChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='100'
                    />
                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    className='bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700'
                  >
                    Add Assignment
                  </button>
                </div>
              </form>
            </div>
          )}

          {assignments.length > 0 ? (
            <div className='space-y-3'>
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className='bg-white p-4 rounded-lg shadow border border-gray-200'
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <h3 className='font-semibold text-gray-900'>
                        {assignment.title}
                      </h3>
                      <p className='text-sm text-gray-600 mt-1'>
                        Due: {new Date(assignment.due_date).toLocaleString()}
                      </p>
                      <p className='text-sm text-gray-600'>
                        Max Score: {assignment.max_score} points
                      </p>
                    </div>
                    <div className='flex items-center space-x-3 ml-4'>
                      <Link
                        to={`/assignments/${assignment.id}/grade`}
                        className='text-primary-600 hover:text-primary-800 text-sm font-medium'
                      >
                        View Submissions
                      </Link>
                      <button
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        className='text-red-600 hover:text-red-800'
                      >
                        <svg
                          className='h-5 w-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-12 bg-gray-50 rounded-lg'>
              <svg
                className='mx-auto h-12 w-12 text-gray-400 mb-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <p className='text-gray-500 mb-4'>No assignments added yet</p>
              <button
                onClick={() => setShowAddAssignment(true)}
                className='text-primary-600 hover:text-primary-700 font-medium'
              >
                Add your first assignment
              </button>
            </div>
          )}
        </div>
      )}

      {/* Quizzes Tab */}
      {activeTab === 'quizzes' && (
        <div>
          <div className='flex justify-between items-center mb-6'>
            <h2 className='text-xl font-semibold'>Course Quizzes</h2>
            <button
              onClick={() => setShowAddQuiz(!showAddQuiz)}
              className='bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700'
            >
              {showAddQuiz ? 'Cancel' : '+ Add Quiz'}
            </button>
          </div>

          {showAddQuiz && (
            <div className='bg-white p-6 rounded-lg shadow mb-6'>
              <h3 className='text-lg font-semibold mb-4'>Add New Quiz</h3>
              <form onSubmit={handleAddQuiz} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Quiz Title *
                  </label>
                  <input
                    type='text'
                    name='title'
                    required
                    value={quizForm.title}
                    onChange={handleQuizChange}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='e.g., Module 1 Quiz'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Description *
                  </label>
                  <textarea
                    name='description'
                    required
                    value={quizForm.description}
                    onChange={handleQuizChange}
                    rows={4}
                    className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                    placeholder='Enter quiz description and instructions...'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Time Limit (minutes) *
                    </label>
                    <input
                      type='number'
                      name='time_limit'
                      required
                      min='1'
                      value={quizForm.time_limit}
                      onChange={handleQuizChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='30'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Passing Score (%) *
                    </label>
                    <input
                      type='number'
                      name='passing_score'
                      required
                      min='0'
                      max='100'
                      value={quizForm.passing_score}
                      onChange={handleQuizChange}
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='70'
                    />
                  </div>
                </div>

                <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4'>
                  <div className='flex'>
                    <div className='flex-shrink-0'>
                      <svg
                        className='h-5 w-5 text-yellow-400'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                      >
                        <path
                          fillRule='evenodd'
                          d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                    <div className='ml-3'>
                      <p className='text-sm text-yellow-700'>
                        Note: After creating the quiz, you'll need to add
                        questions through the Django admin panel or API.
                      </p>
                    </div>
                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    className='bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700'
                  >
                    Add Quiz
                  </button>
                </div>
              </form>
            </div>
          )}

          {quizzes.length > 0 ? (
            <div className='space-y-3'>
              {quizzes.map((quiz) => (
                <div
                  key={quiz.id}
                  className='bg-white p-4 rounded-lg shadow border border-gray-200'
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <h3 className='font-semibold text-gray-900'>
                        {quiz.title}
                      </h3>
                      <p className='text-sm text-gray-600 mt-1'>
                        Time Limit: {quiz.time_limit} minutes
                      </p>
                      <p className='text-sm text-gray-600'>
                        Passing Score: {quiz.passing_score}%
                      </p>
                      <p className='text-sm text-gray-600'>
                        Questions: {quiz.total_questions || 0}
                      </p>
                    </div>
                    <div className='flex items-center space-x-3 ml-4'>
                      <button
                        onClick={() => handleManageQuestions(quiz.id)}
                        className='text-blue-600 hover:text-blue-800 text-sm font-medium'
                      >
                        Manage Questions
                      </button>
                      <Link
                        to={`/quizzes/${quiz.id}/results`}
                        className='text-primary-600 hover:text-primary-800 text-sm font-medium'
                      >
                        View Results
                      </Link>
                      <button
                        onClick={() => handleDeleteQuiz(quiz.id)}
                        className='text-red-600 hover:text-red-800'
                      >
                        <svg
                          className='h-5 w-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-12 bg-gray-50 rounded-lg'>
              <svg
                className='mx-auto h-12 w-12 text-gray-400 mb-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
                />
              </svg>
              <p className='text-gray-500 mb-4'>No quizzes added yet</p>
              <button
                onClick={() => setShowAddQuiz(true)}
                className='text-primary-600 hover:text-primary-700 font-medium'
              >
                Add your first quiz
              </button>
            </div>
          )}
        </div>
      )}

      {/* Question Management Modal */}
      {managingQuizId && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
          <div className='relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-xl font-semibold text-gray-900'>
                Manage Questions
              </h3>
              <button
                onClick={() => setManagingQuizId(null)}
                className='text-gray-400 hover:text-gray-600'
              >
                <svg
                  className='h-6 w-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='mb-4'>
              <button
                onClick={() => setShowAddQuestion(!showAddQuestion)}
                className='bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700'
              >
                {showAddQuestion ? 'Cancel' : '+ Add Question'}
              </button>
            </div>

            {showAddQuestion && (
              <div className='bg-gray-50 p-4 rounded-lg mb-6'>
                <form onSubmit={handleAddQuestion} className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Question Text *
                    </label>
                    <textarea
                      name='question_text'
                      required
                      value={questionForm.question_text}
                      onChange={handleQuestionChange}
                      rows='3'
                      className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      placeholder='Enter the question...'
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Points
                      </label>
                      <input
                        type='number'
                        name='points'
                        min='1'
                        value={questionForm.points}
                        onChange={handleQuestionChange}
                        className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Order
                      </label>
                      <input
                        type='number'
                        name='order'
                        min='0'
                        value={questionForm.order}
                        onChange={handleQuestionChange}
                        className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                      />
                    </div>
                  </div>

                  <div>
                    <div className='flex justify-between items-center mb-2'>
                      <label className='block text-sm font-medium text-gray-700'>
                        Answer Choices *
                      </label>
                      <button
                        type='button'
                        onClick={addAnswer}
                        className='text-sm text-primary-600 hover:text-primary-700'
                      >
                        + Add Answer
                      </button>
                    </div>
                    <div className='space-y-2'>
                      {questionForm.answers.map((answer, index) => (
                        <div
                          key={index}
                          className='flex items-center space-x-2'
                        >
                          <input
                            type='text'
                            value={answer.answer_text}
                            onChange={(e) =>
                              handleAnswerChange(
                                index,
                                'answer_text',
                                e.target.value,
                              )
                            }
                            placeholder={`Answer ${index + 1}`}
                            className='flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500'
                            required
                          />
                          <label className='flex items-center space-x-1'>
                            <input
                              type='checkbox'
                              checked={answer.is_correct}
                              onChange={(e) =>
                                handleAnswerChange(
                                  index,
                                  'is_correct',
                                  e.target.checked,
                                )
                              }
                              className='rounded text-primary-600 focus:ring-primary-500'
                            />
                            <span className='text-sm text-gray-700'>
                              Correct
                            </span>
                          </label>
                          {questionForm.answers.length > 1 && (
                            <button
                              type='button'
                              onClick={() => removeAnswer(index)}
                              className='text-red-600 hover:text-red-800'
                            >
                              <svg
                                className='h-5 w-5'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className='text-xs text-gray-500 mt-1'>
                      Check the box next to the correct answer
                    </p>
                  </div>

                  <button
                    type='submit'
                    className='w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700'
                  >
                    Add Question
                  </button>
                </form>
              </div>
            )}

            {/* Questions List */}
            <div className='max-h-96 overflow-y-auto'>
              {questions.length > 0 ? (
                <div className='space-y-3'>
                  {questions.map((question, index) => (
                    <div
                      key={question.id}
                      className='bg-white p-4 rounded-lg border border-gray-200'
                    >
                      <div className='flex justify-between items-start'>
                        <div className='flex-1'>
                          <div className='flex items-start space-x-2'>
                            <span className='font-semibold text-gray-700'>
                              Q{index + 1}.
                            </span>
                            <p className='text-gray-900'>
                              {question.question_text}
                            </p>
                          </div>
                          <p className='text-sm text-gray-500 mt-1'>
                            Points: {question.points} | Order: {question.order}
                          </p>
                          {question.answers && question.answers.length > 0 && (
                            <div className='mt-2 space-y-1'>
                              {question.answers.map((answer) => (
                                <div
                                  key={answer.id}
                                  className={`text-sm px-2 py-1 rounded ${
                                    answer.is_correct
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {answer.answer_text}
                                  {answer.is_correct && ' ✓'}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteQuestion(question.id)}
                          className='text-red-600 hover:text-red-800 ml-2'
                        >
                          <svg
                            className='h-5 w-5'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='text-center py-8 bg-gray-50 rounded-lg'>
                  <p className='text-gray-500'>No questions added yet</p>
                  <p className='text-sm text-gray-400 mt-1'>
                    Click "Add Question" to create your first question
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageCourse
